import { useAuth } from "@/auth/AuthProvider";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader, PageShell } from "@/components/layout/PageHeader";
import { Button, Input } from "@/components/ui";
import { useState, type FormEvent } from "react";

const MIN_PASSWORD_LENGTH = 8;

export function ChangePasswordPage() {
  const { updatePassword } = useAuth();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setError(null);
    setSuccess(false);

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    const result = await updatePassword(password);
    setSubmitting(false);

    if (result.error) {
      setError(result.error);
      return;
    }

    setPassword("");
    setConfirmPassword("");
    setSuccess(true);
  };

  return (
    <AppLayout>
      <PageShell className="max-w-lg">
        <PageHeader title="Reset password" description="Choose a new password for your account." />

        <div className="rounded-2xl border border-[var(--color-border)] bg-white p-5 shadow-sm sm:p-6">
          {success ? (
            <p className="text-sm text-[var(--color-success)]">Your password has been updated.</p>
          ) : (
            <form className="space-y-4" onSubmit={(event) => void handleSubmit(event)}>
              <label className="block space-y-1.5">
                <span className="text-sm font-medium text-[var(--color-text)]">New password</span>
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  minLength={MIN_PASSWORD_LENGTH}
                  required
                />
              </label>

              <label className="block space-y-1.5">
                <span className="text-sm font-medium text-[var(--color-text)]">
                  Confirm password
                </span>
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  minLength={MIN_PASSWORD_LENGTH}
                  required
                />
              </label>

              <p className="text-xs text-[var(--color-text-muted)]">
                At least {MIN_PASSWORD_LENGTH} characters.
              </p>

              {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}

              <Button type="submit" disabled={submitting}>
                {submitting ? "Saving…" : "Save password"}
              </Button>
            </form>
          )}
        </div>
      </PageShell>
    </AppLayout>
  );
}
